import React, { useEffect, useMemo, useState } from "react";
import { styled } from "styled-components";
import { tokens } from "./tokens";
import { Card, Eyebrow } from "./styled";
import CustomerThresholds from "./CustomerThresholds";
import FieldControl from "./FieldControl";
import ValueChip from "./ValueChip";
import { groupFields } from "./configModel";
import {
  fetchSchemaHistory,
  patchConfigSchema,
  SchemaConflictError,
} from "./configApi";
import {
  ConfigSchema,
  EnumControl,
  Glyph,
  SchemaField,
  SchemaHistoryEntry,
  Severity,
} from "./configTypes";
import {
  bucketsFor,
  buildPatch,
  controlLabel,
  describeDraft,
  describeHistoryChange,
  DraftField,
  DraftOption,
  draftFromSchema,
  editableFields,
  effectiveControl,
  GLYPHS,
  hasHorizon,
  newOption,
  previewField,
  problemWith,
  SchemaDraft,
  SEVERITIES,
  suggestValue,
} from "./schemaEditorModel";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Sheet,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";

/**
 * The Schema editor — where fleet-admins change the record's vocabulary and
 * presentation without a deploy.
 *
 * What is editable here is exactly what `PATCH /v1/config-schema` accepts:
 * labels, help, the control an enum is drawn with, the freshness horizon, and
 * for enums the option list — add, relabel, recolour, reorder, retire. What is
 * not — field names and types, groups, who may write, and the option values the
 * backend derives state from — is shown locked, with the reason, rather than
 * hidden: an admin should be able to see the whole shape and know which parts
 * are theirs. New fields and new customers are a code change and an identity
 * change respectively, and the page says so where someone would look for them.
 *
 * The flow mirrors editing a tractor: change things, watch the preview, review
 * the list of what will change, save. The API keeps the version; a save that
 * lost a race comes back as a conflict with the current schema, and the page
 * offers to load it rather than quietly overwriting someone else's work.
 */
interface SchemaEditorProps {
  schema: ConfigSchema;
  token: string;
  /** Receives the schema the API returned after a save, or the current one after a conflict. */
  onSaved: (schema: ConfigSchema) => void;
  onBack: () => void;
}

/** Values that are tenants, not vocabulary — added on the identity path. */
const IDENTITY_FIELDS = new Set(["customer"]);

const SchemaEditor: React.FC<SchemaEditorProps> = ({
  schema,
  token,
  onSaved,
  onBack,
}) => {
  const [note, setNote] = useState<string | null>(null);
  const [conflict, setConflict] = useState<ConfigSchema | null>(null);
  const version = schema.schema_version ?? 0;
  return (
    <Root>
      <TopBar>
        <BackButton type="button" onClick={onBack}>
          &#8249; All tractors
        </BackButton>
      </TopBar>
      {conflict && (
        <Banner $tone="warn" role="alert">
          <strong>Someone else saved the schema first</strong> — it is now v
          {conflict.schema_version ?? "?"}. Load it to see their change; your
          unsaved edits will be discarded and can be re-applied.
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setConflict(null);
              onSaved(conflict);
            }}
          >
            Load v{conflict.schema_version ?? "?"}
          </Button>
        </Banner>
      )}
      {note && (
        <Banner $tone="ok" role="status">
          {note}
          <Dismiss type="button" onClick={() => setNote(null)}>
            Dismiss
          </Dismiss>
        </Banner>
      )}
      <CustomerThresholds token={token} />
      {/* Keyed on the version: a save or a reload starts the draft over from the
          schema it produced, so there is never a stale draft on a new base. */}
      <EditorBody
        key={version}
        schema={schema}
        token={token}
        onSaved={(next) => {
          setNote(`Saved — the schema is now v${next.schema_version ?? "?"}.`);
          onSaved(next);
        }}
        onConflict={(current) => {
          setNote(null);
          setConflict(current);
        }}
      />
    </Root>
  );
};

const EditorBody: React.FC<{
  schema: ConfigSchema;
  token: string;
  onSaved: (schema: ConfigSchema) => void;
  onConflict: (current: ConfigSchema) => void;
}> = ({ schema, token, onSaved, onConflict }) => {
  const version = schema.schema_version ?? 0;
  const original = useMemo(() => draftFromSchema(schema), [schema]);
  const [draft, setDraft] = useState<SchemaDraft>(original);
  const fields = useMemo(() => editableFields(schema), [schema]);
  const byName = useMemo(
    () => new Map(fields.map((f) => [f.name, f])),
    [fields],
  );
  const groups = useMemo(
    () => groupFields(fields, undefined, schema.groups),
    [fields, schema.groups],
  );
  const [selected, setSelected] = useState<string>(() => fields[0]?.name ?? "");
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<SchemaHistoryEntry[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const changes = useMemo(
    () => describeDraft(original, draft),
    [original, draft],
  );
  const problems = useMemo(() => {
    const out: Record<string, string> = {};
    for (const d of Object.values(draft)) {
      const p = problemWith(d);
      if (p) out[d.name] = p;
    }
    return out;
  }, [draft]);
  const canReview = changes.length > 0 && Object.keys(problems).length === 0;

  const update = (name: string, patch: Partial<DraftField>) =>
    setDraft((prev) => ({ ...prev, [name]: { ...prev[name], ...patch } }));

  const updateOption = (
    name: string,
    index: number,
    patch: Partial<DraftOption>,
  ) =>
    setDraft((prev) => {
      const d = prev[name];
      if (!d.options) return prev;
      const options = d.options.map((o, i) =>
        i === index ? { ...o, ...patch } : o,
      );
      return { ...prev, [name]: { ...d, options } };
    });

  /** A new option's value follows its label until the admin types a value. */
  const relabelOption = (name: string, index: number, label: string) =>
    setDraft((prev) => {
      const d = prev[name];
      if (!d.options) return prev;
      const options = d.options.map((o, i) => {
        if (i !== index) return o;
        const others = d
          .options!.filter((_, j) => j !== index)
          .map((x) => x.value);
        const following =
          o.isNew &&
          (o.value === "" || o.value === suggestValue(o.label, others));
        return {
          ...o,
          label,
          value: following ? suggestValue(label, others) : o.value,
        };
      });
      return { ...prev, [name]: { ...d, options } };
    });

  const moveOption = (name: string, index: number, dir: -1 | 1) =>
    setDraft((prev) => {
      const d = prev[name];
      if (!d.options) return prev;
      const to = index + dir;
      if (to < 0 || to >= d.options.length) return prev;
      const options = [...d.options];
      [options[index], options[to]] = [options[to], options[index]];
      return { ...prev, [name]: { ...d, options } };
    });

  const addOption = (name: string) =>
    setDraft((prev) => {
      const d = prev[name];
      if (!d.options) return prev;
      const taken = d.options.map((o) => o.value);
      return {
        ...prev,
        [name]: { ...d, options: [...d.options, newOption("", taken)] },
      };
    });

  const removeNewOption = (name: string, index: number) =>
    setDraft((prev) => {
      const d = prev[name];
      if (!d.options || !d.options[index]?.isNew) return prev;
      return {
        ...prev,
        [name]: { ...d, options: d.options.filter((_, i) => i !== index) },
      };
    });

  const openHistory = async () => {
    setHistoryOpen(true);
    if (history) return;
    try {
      setHistory((await fetchSchemaHistory(token)).history);
    } catch (e) {
      setHistoryError(
        e instanceof Error ? e.message : "Couldn’t load the history.",
      );
    }
  };

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const next = await patchConfigSchema(
        token,
        buildPatch(original, draft, version),
      );
      setReviewing(false);
      onSaved(next);
    } catch (e) {
      if (e instanceof SchemaConflictError) {
        setReviewing(false);
        onConflict(e.current);
      } else {
        setSaveError(e instanceof Error ? e.message : "The save failed.");
      }
    } finally {
      setSaving(false);
    }
  };

  const field = byName.get(selected);
  const d = draft[selected];

  return (
    <>
      <Head>
        <div>
          <Title>Schema</Title>
          <Sub>
            v{version} · {fields.length} fields · fleet-admins only
          </Sub>
        </div>
        <HeadActions>
          <Button variant="quiet" size="sm" onClick={openHistory}>
            History
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={changes.length === 0}
            onClick={() => setDraft(original)}
          >
            Discard
          </Button>
          <Button
            size="sm"
            disabled={!canReview}
            onClick={() => setReviewing(true)}
          >
            {changes.length === 0
              ? "No changes yet"
              : `Review ${changes.length} change${changes.length === 1 ? "" : "s"}`}
          </Button>
        </HeadActions>
      </Head>

      <Body>
        <Rail aria-label="Fields">
          {groups.map((g) => (
            <React.Fragment key={g.key}>
              <RailGroup>{g.label || "Assignment"}</RailGroup>
              {g.fields.map((f) => {
                const edits = changes.filter((c) => c.field === f.name).length;
                const active = f.name === selected;
                return (
                  <RailItem
                    key={f.name}
                    type="button"
                    $active={active}
                    aria-current={active ? "true" : undefined}
                    onClick={() => setSelected(f.name)}
                  >
                    <span>{draft[f.name]?.label ?? f.label}</span>
                    {problems[f.name] ? (
                      <RailFlag $active={active} title={problems[f.name]}>
                        !
                      </RailFlag>
                    ) : edits > 0 ? (
                      <RailFlag $active={active}>
                        {edits} edit{edits === 1 ? "" : "s"}
                      </RailFlag>
                    ) : IDENTITY_FIELDS.has(f.name) ? (
                      <RailLock title="Values are tenants — added on the identity path">
                        identity
                      </RailLock>
                    ) : null}
                  </RailItem>
                );
              })}
            </React.Fragment>
          ))}
          <RailNote>
            New fields, types and groups are a code change — the record’s shape
            is engineering’s; its vocabulary is yours.
          </RailNote>
        </Rail>

        {field && d ? (
          <>
            <Main>
              <Eyebrow>
                Field ·{" "}
                {groups.find((g) => g.fields.includes(field))?.label ||
                  "Assignment"}
              </Eyebrow>
              <FieldTitle>
                {d.label || field.label}
                <Mono>
                  {field.name} · {field.type}
                  {field.editable_by.length
                    ? ` · ${field.editable_by.join(" / ")}`
                    : ""}
                </Mono>
              </FieldTitle>

              <Form>
                <label>
                  <FormLabel>Label</FormLabel>
                  <Input
                    value={d.label}
                    onChange={(e) =>
                      update(field.name, { label: e.target.value })
                    }
                  />
                </label>
                <label>
                  <FormLabel>Help</FormLabel>
                  <Input
                    value={d.help}
                    placeholder="A sentence under the control, when one is needed"
                    onChange={(e) =>
                      update(field.name, { help: e.target.value })
                    }
                  />
                </label>
                {field.type === "enum" && (
                  <div>
                    <FormLabel id={`control-${field.name}`}>Control</FormLabel>
                    <Choices
                      role="radiogroup"
                      aria-labelledby={`control-${field.name}`}
                    >
                      {(
                        [
                          [
                            null,
                            `Auto (${controlLabel(effectiveControl({ ...d, control: null }))})`,
                          ],
                          ["segmented", "Chiclets"],
                          ["select", "Dropdown"],
                        ] as [EnumControl | null, string][]
                      ).map(([value, label]) => (
                        <Choice
                          key={label}
                          type="button"
                          role="radio"
                          aria-checked={d.control === value}
                          $on={d.control === value}
                          onClick={() => update(field.name, { control: value })}
                        >
                          {label}
                        </Choice>
                      ))}
                    </Choices>
                    <FormNote>
                      Four options or fewer read best as chiclets; a long list
                      reads as a dropdown.
                    </FormNote>
                  </div>
                )}
                {hasHorizon(field) && (
                  <label>
                    <FormLabel>Freshness horizon (days)</FormLabel>
                    <Input
                      type="number"
                      min={1}
                      step={1}
                      value={d.stale_after_days ?? ""}
                      onChange={(e) =>
                        update(field.name, {
                          stale_after_days:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        })
                      }
                    />
                    <FormNote>
                      After this many days the console flags the value as stale
                      — when the freshness chrome is on.
                    </FormNote>
                  </label>
                )}
              </Form>

              {field.type === "enum" && d.options && (
                <Options>
                  <OptionsHead>
                    <Eyebrow>Options · {d.options.length}</Eyebrow>
                    {IDENTITY_FIELDS.has(field.name) ? (
                      <Badge tone="neutral">
                        identity — values are tenants
                      </Badge>
                    ) : (
                      <Button
                        variant="quiet"
                        size="sm"
                        onClick={() => addOption(field.name)}
                      >
                        + Add option
                      </Button>
                    )}
                  </OptionsHead>
                  {IDENTITY_FIELDS.has(field.name) ? (
                    <FormNote>
                      A customer is a tenant with its own people and
                      permissions. Adding one is an identity change, not a
                      vocabulary change — ask the platform team.
                    </FormNote>
                  ) : (
                    <TableWrap>
                      <table>
                        <thead>
                          <tr>
                            <th aria-label="Order" />
                            <th>Value</th>
                            <th>Label</th>
                            <th>Colour</th>
                            <th>Glyph</th>
                            {bucketsFor(field).length > 0 && (
                              <th>Counts toward</th>
                            )}
                            <th>Retired</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.options.map((o, i) => (
                            <tr
                              // The row's own id, not its value: a new
                              // option's value changes on every keystroke of
                              // its label, and a key that changes replaces
                              // the row -- and the input being typed in
                              // (SW-7539).
                              key={o.id}
                              className={
                                o.isNew
                                  ? "new"
                                  : o.retired
                                    ? "retired"
                                    : undefined
                              }
                            >
                              <td>
                                <Order>
                                  <Nudge
                                    type="button"
                                    aria-label={`Move ${o.label || o.value} up`}
                                    disabled={i === 0}
                                    onClick={() =>
                                      moveOption(field.name, i, -1)
                                    }
                                  >
                                    ▲
                                  </Nudge>
                                  <Nudge
                                    type="button"
                                    aria-label={`Move ${o.label || o.value} down`}
                                    disabled={i === d.options!.length - 1}
                                    onClick={() => moveOption(field.name, i, 1)}
                                  >
                                    ▼
                                  </Nudge>
                                </Order>
                              </td>
                              <td>
                                {o.isNew ? (
                                  <Input
                                    aria-label="New option value"
                                    value={o.value}
                                    className="mono"
                                    onChange={(e) =>
                                      updateOption(field.name, i, {
                                        value: e.target.value
                                          .toUpperCase()
                                          .replace(/\s+/g, "_"),
                                      })
                                    }
                                  />
                                ) : (
                                  <ValueCell>
                                    {o.value}
                                    {o.protected && (
                                      <Badge
                                        tone="warn"
                                        title="The console derives a tractor’s state from this value — relabel it, never retire it."
                                      >
                                        protected
                                      </Badge>
                                    )}
                                  </ValueCell>
                                )}
                              </td>
                              <td>
                                <Input
                                  aria-label={`Label for ${o.value || "new option"}`}
                                  value={o.label}
                                  onChange={(e) =>
                                    relabelOption(field.name, i, e.target.value)
                                  }
                                />
                              </td>
                              <td>
                                <Select
                                  aria-label={`Colour for ${o.value}`}
                                  value={o.severity}
                                  onChange={(e) =>
                                    updateOption(field.name, i, {
                                      severity: e.target.value as Severity,
                                    })
                                  }
                                >
                                  {SEVERITIES.map((s) => (
                                    <option key={s} value={s}>
                                      {s}
                                    </option>
                                  ))}
                                </Select>
                              </td>
                              <td>
                                <Select
                                  aria-label={`Glyph for ${o.value}`}
                                  value={o.glyph ?? ""}
                                  onChange={(e) =>
                                    updateOption(field.name, i, {
                                      glyph: (e.target.value ||
                                        null) as Glyph | null,
                                    })
                                  }
                                >
                                  <option value="">none</option>
                                  {GLYPHS.map((g) => (
                                    <option key={g} value={g}>
                                      {g}
                                    </option>
                                  ))}
                                </Select>
                              </td>
                              {bucketsFor(field).length > 0 && (
                                <td>
                                  <Select
                                    aria-label={`Counts toward for ${o.value}`}
                                    value={o.rollup_bucket ?? ""}
                                    onChange={(e) =>
                                      updateOption(field.name, i, {
                                        rollup_bucket: e.target.value || null,
                                      })
                                    }
                                  >
                                    <option value="">—</option>
                                    {bucketsFor(field).map((b) => (
                                      <option key={b} value={b}>
                                        {b}
                                      </option>
                                    ))}
                                  </Select>
                                </td>
                              )}
                              <td>
                                {o.isNew ? (
                                  <Button
                                    variant="quiet"
                                    size="sm"
                                    onClick={() =>
                                      removeNewOption(field.name, i)
                                    }
                                  >
                                    Remove
                                  </Button>
                                ) : (
                                  <input
                                    type="checkbox"
                                    aria-label={`Retire ${o.label}`}
                                    checked={o.retired}
                                    disabled={o.protected}
                                    onChange={(e) =>
                                      updateOption(field.name, i, {
                                        retired: e.target.checked,
                                      })
                                    }
                                  />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </TableWrap>
                  )}
                  {problems[field.name] ? (
                    <Problem role="alert">{problems[field.name]}</Problem>
                  ) : (
                    <FormNote>
                      A value is the key stored on every tractor and never
                      changes once saved; the label is what people read. A
                      retired option stops being offered but keeps rendering on
                      tractors that hold it.
                    </FormNote>
                  )}
                </Options>
              )}
            </Main>

            <Preview>
              <Eyebrow>Preview · as ops will see it</Eyebrow>
              <PreviewPane field={previewField(field, d)} />
            </Preview>
          </>
        ) : (
          <Main>
            <Empty>Pick a field to edit it.</Empty>
          </Main>
        )}
      </Body>

      <Dialog
        open={reviewing}
        onOpenChange={(open) => !saving && setReviewing(open)}
      >
        <DialogContent>
          <DialogTitle>
            Save {changes.length} schema change{changes.length === 1 ? "" : "s"}
            ?
          </DialogTitle>
          <DialogDescription>
            Everyone’s console picks these up within a minute. Tractors keep the
            values they have; nothing is rewritten.
          </DialogDescription>
          <ChangeList>
            {changes.map((c, i) => (
              <li key={i}>
                <ChangeField>{c.fieldLabel}</ChangeField>
                <span>{c.text}</span>
              </li>
            ))}
          </ChangeList>
          {saveError && <Problem role="alert">{saveError}</Problem>}
          <DialogFooter>
            <Button
              variant="ghost"
              size="sm"
              disabled={saving}
              onClick={() => setReviewing(false)}
            >
              Back to editing
            </Button>
            <Button size="sm" disabled={saving} onClick={save}>
              {saving
                ? "Saving…"
                : `Save ${changes.length} change${changes.length === 1 ? "" : "s"}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent>
          {/* The header is a row: the title block on the left, the way out on
              the right. Title and description as bare siblings shared that row
              instead -- the description was pushed to the far edge, and at
              phone width the title wrapped beside it. The Close button matters
              most there too: the sheet is the full screen, so there is no
              outside to tap and no Escape key to press. */}
          <SheetHeader>
            <div>
              <SheetTitle>Schema history</SheetTitle>
              <SheetDescription>
                Who changed the vocabulary, and what — newest first.
              </SheetDescription>
            </div>
            <SheetClose asChild>
              <Button variant="ghost" size="sm">
                Close
              </Button>
            </SheetClose>
          </SheetHeader>
          {/* The body has no padding of its own; the change-history tree brings
              its own, these notes and cards do not. */}
          <SheetBody className="px-5 py-4">
            {historyError ? (
              <Problem role="alert">{historyError}</Problem>
            ) : history === null ? (
              <FormNote>Loading…</FormNote>
            ) : history.length === 0 ? (
              <FormNote>
                No schema changes recorded yet. The code’s vocabulary is what
                everyone sees.
              </FormNote>
            ) : (
              <HistoryList>
                {history.map((h) => (
                  <li key={h.schema_version}>
                    <HistoryHead>
                      <strong>v{h.schema_version}</strong>
                      <span>{h.at.slice(0, 16).replace("T", " ")}</span>
                      <span>{h.actor_email ?? "unknown"}</span>
                    </HistoryHead>
                    <ul>
                      {h.changes.map((c, i) => (
                        <li key={i}>{describeHistoryChange(c, schema)}</li>
                      ))}
                    </ul>
                  </li>
                ))}
              </HistoryList>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
};

/** The draft rendered by the real controls, with a value the admin can poke. */
const PreviewPane: React.FC<{ field: SchemaField }> = ({ field }) => {
  const first = field.options?.find((o) => !o.retired)?.value ?? "";
  const [value, setValue] = useState<string | string[]>(first);
  useEffect(() => {
    // Keep the poked value if it still exists; otherwise fall back.
    if (
      typeof value === "string" &&
      field.options &&
      !field.options.some((o) => o.value === value)
    ) {
      setValue(first);
    }
  }, [field, first, value]);
  return (
    <>
      <PreviewLabel>Editing a tractor</PreviewLabel>
      <FieldControl field={field} value={value} onChange={(v) => setValue(v)} />
      <PreviewLabel>Reading a tractor</PreviewLabel>
      <div>
        <ValueChip
          field={field}
          value={typeof value === "string" ? value : null}
        />
      </div>
      {field.help && (
        <>
          <PreviewLabel>Help</PreviewLabel>
          <FormNote>{field.help}</FormNote>
        </>
      )}
    </>
  );
};

// --------------------------------------------------------------------------

const Root = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 24px 40px 32px;
  @media (max-width: 760px) {
    padding: 20px 16px 48px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.slate};
  cursor: pointer;
  &:hover {
    color: ${tokens.blue};
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: 3px;
    border-radius: 3px;
  }
`;

const Head = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 34px;
  font-weight: 300;
  letter-spacing: -0.02em;
`;

const Sub = styled.div`
  font-size: 12.5px;
  color: ${tokens.slate};
  margin-top: 2px;
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Banner = styled.div<{ $tone: "warn" | "ok" }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 12px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.45;
  color: ${(p) => (p.$tone === "warn" ? tokens.majorText : tokens.readyText)};
  background: ${(p) =>
    p.$tone === "warn" ? "rgba(255,150,0,0.1)" : "rgba(8,116,0,0.08)"};
  border: 1px solid
    ${(p) =>
      p.$tone === "warn" ? "rgba(255,150,0,0.35)" : "rgba(8,116,0,0.3)"};
`;

const Dismiss = styled.button`
  margin-left: auto;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-weight: 600;
  color: ${tokens.blue};
  cursor: pointer;
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) 300px;
  gap: 16px;
  align-items: start;
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Rail = styled(Card).attrs({ as: "nav" })`
  padding: 12px 10px;
  display: flex;
  flex-direction: column;
`;

const RailGroup = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${tokens.navy};
  padding: 10px 8px 6px;
`;

const RailItem = styled.button<{ $active: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
  text-align: left;
  border: none;
  border-radius: 8px;
  padding: 7px 10px;
  font: inherit;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background: ${(p) => (p.$active ? tokens.blue : "transparent")};
  color: ${(p) => (p.$active ? tokens.white : tokens.ink)};
  &:hover {
    background: ${(p) => (p.$active ? tokens.blue : tokens.offWhite)};
  }
  &:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 2px ${tokens.white},
      0 0 0 4px ${tokens.blue};
  }
  > span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const RailFlag = styled.span<{ $active: boolean }>`
  flex: none;
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 999px;
  background: ${(p) => (p.$active ? tokens.white : tokens.offWhite)};
  color: ${(p) => (p.$active ? tokens.blue : tokens.slate)};
  border: 1px solid ${(p) => (p.$active ? tokens.white : tokens.line)};
`;

const RailLock = styled.span`
  flex: none;
  font-size: 11px;
  color: ${tokens.slate};
`;

const RailNote = styled.div`
  margin: 14px 8px 4px;
  font-size: 12px;
  line-height: 1.45;
  color: ${tokens.slate};
`;

const Main = styled(Card)`
  min-width: 0;
  padding: 18px 20px 20px;
`;

const FieldTitle = styled.div`
  font-size: 22px;
  font-weight: 300;
  letter-spacing: -0.01em;
  margin: 4px 0 14px;
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const Mono = styled.span`
  font-family: ${tokens.fontMono};
  font-size: 12px;
  color: ${tokens.slate};
`;

const Form = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 280px), 1fr));
  gap: 14px 22px;
  label {
    display: block;
  }
`;

const FormLabel = styled.div`
  font-size: 12.5px;
  color: ${tokens.slate};
  margin-bottom: 4px;
`;

const FormNote = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${tokens.slate};
  margin-top: 6px;
`;

const Choices = styled.div`
  display: inline-flex;
  border: 1px solid ${tokens.line};
  border-radius: 8px;
  overflow: hidden;
`;

const Choice = styled.button<{ $on: boolean }>`
  border: none;
  border-right: 1px solid ${tokens.line};
  padding: 7px 12px;
  font: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$on ? "rgba(0,105,255,0.08)" : tokens.white)};
  color: ${(p) => (p.$on ? tokens.blue : tokens.slate)};
  &:last-child {
    border-right: none;
  }
  &:focus-visible {
    outline: 2px solid ${tokens.blue};
    outline-offset: -2px;
  }
`;

const Options = styled.div`
  margin-top: 20px;
`;

const OptionsHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  table {
    width: 100%;
    min-width: 760px;
    border-collapse: separate;
    border-spacing: 0;
    font-size: 12.5px;
  }
  th {
    text-align: left;
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${tokens.navy};
    font-weight: 700;
    padding: 6px 8px;
    border-bottom: 1px solid ${tokens.line};
    background: ${tokens.offWhite};
    white-space: nowrap;
  }
  td {
    padding: 6px 8px;
    border-bottom: 1px solid ${tokens.line};
    vertical-align: middle;
  }
  tr.new td {
    background: rgba(0, 105, 255, 0.04);
  }
  tr.retired td {
    color: ${tokens.slate};
  }
  input.mono {
    font-family: ${tokens.fontMono};
    font-size: 12px;
  }
`;

const Order = styled.div`
  display: inline-flex;
  flex-direction: column;
`;

const Nudge = styled.button`
  border: none;
  background: none;
  padding: 0 4px;
  font-size: 9px;
  line-height: 1.2;
  color: ${tokens.slate};
  cursor: pointer;
  &:disabled {
    opacity: 0.3;
    cursor: default;
  }
  &:hover:not(:disabled) {
    color: ${tokens.blue};
  }
`;

const ValueCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${tokens.fontMono};
  font-size: 12px;
  white-space: nowrap;
`;

const Select = styled.select`
  font: inherit;
  font-size: 12.5px;
  padding: 5px 8px;
  border: 1px solid ${tokens.inputLine};
  border-radius: 6px;
  background: ${tokens.white};
  color: ${tokens.ink};
`;

const Problem = styled.div`
  margin-top: 8px;
  font-size: 12.5px;
  color: ${tokens.groundedText};
`;

const Preview = styled(Card)`
  padding: 14px 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: sticky;
  top: 16px;
`;

const PreviewLabel = styled.div`
  font-size: 12px;
  color: ${tokens.slate};
  margin-top: 8px;
`;

const Empty = styled.div`
  font-size: 13px;
  color: ${tokens.slate};
`;

const ChangeList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: grid;
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
  li {
    display: grid;
    gap: 2px;
    padding: 8px 10px;
    border: 1px solid ${tokens.line};
    border-radius: 8px;
    font-size: 13px;
  }
`;

const ChangeField = styled.span`
  font-size: 11.5px;
  color: ${tokens.slate};
`;

const HistoryList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 12px;
  > li {
    border: 1px solid ${tokens.line};
    border-radius: 10px;
    padding: 10px 12px;
  }
  ul {
    margin: 6px 0 0;
    padding-left: 16px;
    font-size: 12.5px;
  }
`;

const HistoryHead = styled.div`
  display: flex;
  gap: 10px;
  align-items: baseline;
  font-size: 12.5px;
  color: ${tokens.slate};
  strong {
    color: ${tokens.ink};
  }
`;

export default SchemaEditor;
